import * as React from 'react';
import {
  DataContainer,
  DataIndicator,
  MainDataValue,
  GrowIndicator,
  TopData,
  BottomData,
} from './ComparisonWidget.styles';
import {
  Container,
  Section,
  Header,
  Column,
  Title,
  SecondaryDataValue,
  ValueContainer,
} from '../Widgets.styles';
import { IComparisonWidgetData, ICurrencyFormat, IWidget } from 'utils/types';
import { getWidgetFontSizeByValue } from '../widget-utils';
import {
  getFormattedCurrency,
  getFormattedPercentage,
  getFormattedRange,
} from 'utils/format';

const MAX_SECONDARY_VALUE_FONT_SIZE = 23;

interface IWidgetProps extends IWidget {
  title: string;
}

export const ComparisonWidget = ({
  data,
  format,
  highlightedPart,
  isYTD = false,
}: {
  data: IWidgetProps[];
  format: ICurrencyFormat;
  highlightedPart?: number[] | null;
  isYTD?: boolean;
}) => (
  <Container>
    {data.map((item, index: number) => {
      const {
        currentDateRange,
        currentValue,
        comparisonDateRange,
        comparisonValue,
        netChange,
        title,
        variance,
      } = item;
      const currentPeriodStr = getFormattedRange(
        currentDateRange,
        Boolean(isYTD)
      );
      const prevPeriodStr = getFormattedRange(comparisonDateRange);
      const varianceStr = getFormattedPercentage(variance, format);
      let highlightedIndex = undefined;
      if (highlightedPart && highlightedPart[1] === index) {
        highlightedIndex = highlightedPart[0];
      }
      const formattedCurrentValue =
        currentValue && format
          ? getFormattedCurrency({
              currencyFormat: format,
              value: currentValue,
              isCurrencySign: true,
              isIdealDenomination: true,
              isUnitSign: true,
            })
          : '-';
      const formattedComparisonValue =
        comparisonValue && format
          ? getFormattedCurrency({
              currencyFormat: format,
              value: comparisonValue,
              isCurrencySign: true,
              isIdealDenomination: true,
              isUnitSign: true,
            })
          : '-';

      return (
        <ComparisonWidgetSectionView
          key={`${title}-${currentPeriodStr}`}
          title={title}
          highlightedIndex={highlightedIndex}
          currentPeriod={currentPeriodStr}
          currentValue={formattedCurrentValue}
          previousPeriod={prevPeriodStr}
          previousValue={formattedComparisonValue}
          netChange={netChange || 0}
          variance={varianceStr}
          format={format}
        />
      );
    })}
  </Container>
);

export const ComparisonWidgetSectionView = ({
  title,
  highlightedIndex,
  currentPeriod,
  currentValue,
  previousPeriod,
  previousValue,
  netChange,
  variance,
  format,
}: IComparisonWidgetData) => {
  const mainValueFontSize = getWidgetFontSizeByValue({
    value: currentValue,
  });
  const formattedNetChangeValue =
    netChange && format
      ? getFormattedCurrency({
          currencyFormat: format,
          value: Math.abs(netChange),
          isCurrencySign: true,
          isUnitSign: true,
          isIdealDenomination: true,
        })
      : '-';
  return (
    <Section>
      <Header>{title}</Header>
      <DataContainer>
        <TopData>
          <Column isHighlighted={highlightedIndex === 0}>
            <Title>{currentPeriod} </Title>
            <ValueContainer>
              <DataIndicator primary />
              <MainDataValue
                fontSize={getWidgetFontSizeByValue({
                  value: previousValue,
                  maxSize: Math.min(
                    mainValueFontSize,
                    MAX_SECONDARY_VALUE_FONT_SIZE
                  ),
                })}
              >
                {currentValue}
              </MainDataValue>
            </ValueContainer>
          </Column>
          <Column isHighlighted={highlightedIndex === 1}>
            <Title>{previousPeriod} </Title>
            <ValueContainer>
              <DataIndicator />
              <SecondaryDataValue
                fontSize={getWidgetFontSizeByValue({
                  value: previousValue,
                  maxSize: Math.min(
                    mainValueFontSize,
                    MAX_SECONDARY_VALUE_FONT_SIZE
                  ),
                })}
              >
                {previousValue}
              </SecondaryDataValue>
            </ValueContainer>
          </Column>
        </TopData>
        <BottomData>
          <Column>
            <Title>Net change</Title>
            <ValueContainer>
              {netChange !== 0 && netChange !== null ? (
                <GrowIndicator
                  iconName={netChange > 0 ? 'arrow_upward' : 'arrow_downward'}
                  grow={netChange > 0}
                />
              ) : null}
              <SecondaryDataValue
                fontSize={getWidgetFontSizeByValue({
                  value: formattedNetChangeValue,
                  maxSize: Math.min(
                    mainValueFontSize,
                    MAX_SECONDARY_VALUE_FONT_SIZE
                  ),
                })}
              >
                {formattedNetChangeValue}
              </SecondaryDataValue>
            </ValueContainer>
          </Column>
          <Column>
            <Title>Variance</Title>
            <ValueContainer>
              <SecondaryDataValue
                fontSize={getWidgetFontSizeByValue({
                  value: variance,
                  maxSize: Math.min(
                    mainValueFontSize,
                    MAX_SECONDARY_VALUE_FONT_SIZE
                  ),
                })}
              >
                {variance}
              </SecondaryDataValue>
            </ValueContainer>
          </Column>
        </BottomData>
      </DataContainer>
    </Section>
  );
};

export default ComparisonWidget;
